import LetterGlitch from '@/taskpane/components/LetterGlitch';
import { Fingerprint, FolderLock, Save, Share2 } from 'lucide-react';
import type { ReactElement } from 'react';

import { AppSourceButton } from './AppSourceButton';
import { AUTH_STEPS, FEATURES, INSTALL_STEPS } from './constants';
import { HostsSection } from './HostsSection';
import { IvaltAuthDemo } from './IvaltAuthDemo';
import { IvaltDownloadBlock } from './IvaltDownloadBlock';
import { LandingFooter } from './LandingFooter';
import { LandingHeader } from './LandingHeader';
import { MagicCard } from './MagicCard';
import { OfficeWindowPreview } from './OfficeWindowPreview';
import { Reveal } from './Reveal';

const FEATURE_ICONS = [Fingerprint, FolderLock, Save, Share2] as const;

export function LandingPage(): ReactElement {
  return (
    <div className="bg-background text-foreground min-h-screen md:px-4">
      <LandingHeader />

      <main>
        <section className="relative overflow-hidden md:-mx-4">
          <div className="absolute inset-0 z-0">
            <LetterGlitch
              glitchColors={['#26251e', '#f54e00', '#5a5852', '#3a3832']}
              glitchSpeed={80}
              centerVignette={false}
              outerVignette
              smooth
            />
            <div className="from-background/95 via-background/80 to-background absolute inset-0 bg-gradient-to-b" />
          </div>
          <div className="relative z-10 mx-auto grid max-w-5xl items-center gap-10 px-5 pt-8 pb-16 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] md:pt-12">
            <Reveal variant="left">
              <div>
                <span className="bg-primary/15 text-primary mb-4 inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold">
                  <span className="relative flex size-2">
                    <span className="bg-primary/60 absolute inset-0 animate-ping rounded-full" />
                    <span className="bg-primary relative size-2 rounded-full" />
                  </span>
                  Available for Microsoft Office
                </span>
                <p className="text-primary mb-3 font-mono text-[11px] tracking-[0.18em] uppercase">
                  Word · Excel · PowerPoint
                </p>
                <h1 className="text-foreground text-[2rem] leading-[1.15] font-semibold tracking-tight sm:text-4xl">
                  Your vault, in the Office sidebar
                </h1>
                <p className="text-muted-foreground mt-4 max-w-md text-sm leading-relaxed">
                  One AppSource listing, three host panes. Word only lists documents, Excel only
                  lists workbooks, PowerPoint only lists decks. Open a file into the app you are
                  already in.
                </p>
                <div className="mt-6">
                  <AppSourceButton />
                </div>
              </div>
            </Reveal>
            <Reveal variant="right">
              <OfficeWindowPreview />
            </Reveal>
          </div>
        </section>

        <HostsSection />

        <section id="sign-in">
          <Reveal>
            <div className="mx-auto grid max-w-5xl gap-12 px-5 py-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:items-center">
              <div>
                <p className="text-primary mb-3 font-mono text-[11px] tracking-[0.18em] uppercase">
                  Authentication
                </p>
                <h2 className="text-foreground text-xl font-semibold tracking-tight">
                  Sign in with biometrics, not a password
                </h2>
                <p className="text-muted-foreground mt-2 max-w-xl text-sm leading-relaxed">
                  The sidebar does not use passwords. It authenticates you through iVALT biometric
                  verification, then derives your encryption keys locally on this device.
                </p>
                <ol className="mt-8 grid gap-3">
                  {AUTH_STEPS.map((step, index) => (
                    <li key={step.title}>
                      <MagicCard className="flex h-full flex-col rounded-2xl">
                        <div className="flex gap-4 p-5">
                          <span className="text-primary mt-0.5 font-mono text-xs font-semibold">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div>
                            <h3 className="text-foreground text-sm font-semibold">{step.title}</h3>
                            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                              {step.body}
                            </p>
                          </div>
                        </div>
                      </MagicCard>
                    </li>
                  ))}
                </ol>
                <IvaltDownloadBlock />
                <p className="text-muted-foreground mt-4 text-center text-xs">
                  Sign in with the phone number registered in your DocuID account.
                </p>
              </div>
              <div className="flex justify-center lg:justify-end">
                <IvaltAuthDemo />
              </div>
            </div>
          </Reveal>
        </section>

        <section>
          <Reveal>
            <div className="mx-auto max-w-5xl px-5 py-14">
              <h2 className="text-foreground text-xl font-semibold tracking-tight">
                What the pane actually does
              </h2>
              <p className="text-muted-foreground mt-2 max-w-xl text-sm">
                Same actions as the dashboard, scoped to the file in front of you.
              </p>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                {FEATURES.map((feature, index) => {
                  const Icon = FEATURE_ICONS[index] ?? Fingerprint;
                  return (
                    <li key={feature.title}>
                      <MagicCard className="flex h-full flex-col rounded-2xl">
                        <div className="p-5">
                          <Icon className="text-primary mb-3 size-4" />
                          <h3 className="text-foreground text-sm font-semibold">{feature.title}</h3>
                          <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                            {feature.body}
                          </p>
                        </div>
                      </MagicCard>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Reveal>
        </section>

        <section id="how-it-opens">
          <Reveal>
            <div className="mx-auto max-w-5xl px-5 py-14">
              <h2 className="text-foreground text-xl font-semibold tracking-tight">
                How to open it in Office
              </h2>
              <p className="text-muted-foreground mt-2 max-w-xl text-sm">
                This page is only the download site. DocuID runs inside Word, Excel, or PowerPoint
                after you install it.
              </p>
              <ol className="mt-8 grid gap-3">
                {INSTALL_STEPS.map((step, index) => (
                  <li
                    key={step.title}
                    className="border-border bg-card flex gap-4 rounded-2xl border px-4 py-4"
                  >
                    <span className="text-primary mt-0.5 font-mono text-xs font-semibold">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="text-foreground text-sm font-semibold">{step.title}</h3>
                      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                        {step.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}
